import React, {useEffect, useState} from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Chip,
  Stack,
  Paper,
  Typography,
  Button,
  Divider
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

export type PermissionItem = {
  value: string;
  description: string;
};

export type AvailablePermissionSet = {
  identifier: string;
  availablePermissions: PermissionItem[];
};

export type PermissionSelection = Record<string, string[]>;

type Props = {
  data: AvailablePermissionSet[];
  value: PermissionSelection;
  onChange?: (next: PermissionSelection) => void;
  title?: string;
  disabled?: boolean;
};

export function PermissionToggleList({
                                       data,
                                       value,
                                       onChange,
                                       title,
                                       disabled = false,
                                     }: Props) {

  const [localSelection, setLocalSelection] = useState<PermissionSelection>({});

  useEffect(() => {
    setLocalSelection({ ...value });
  }, [value])

  const toggle = (apiIdentifier: string, perm: PermissionItem) => {
    if (disabled) return;

    const s = {...localSelection};
    if (s[apiIdentifier]) {
      const current = new Set(localSelection[apiIdentifier] ?? []);
      current.has(perm.value) ? current.delete(perm.value) : current.add(perm.value);
      s[apiIdentifier] = Array.from(current);
    } else {
      s[apiIdentifier] = [perm.value];
    }

    if (onChange) {
      onChange(s);
    }

    setLocalSelection(s);
  };

  const selectAll = (api: AvailablePermissionSet) => {
    if (disabled) return;

    const s = {...localSelection};
    s[api.identifier] = data.find((a) => {
      return a.identifier === api.identifier;
    })?.availablePermissions.map((p) => {
      return p.value
    }) || [];

    setLocalSelection(s);
  };

  const clearAll = (apiName: string) => {
    if (disabled) return;

    const s = {...localSelection};
    s[apiName] = [];

    setLocalSelection(s);
  };

  const isSelected = (apiName: string, perm: PermissionItem) =>
    (localSelection[apiName] ?? []).includes(perm.value);

  return (
    <Paper variant="outlined">
      <List
        subheader={
          title ? (
            <ListSubheader component="div" disableSticky>
              {title}
            </ListSubheader>
          ) : undefined
        }
      >
        {
          data.map((api, idx) => (
            <React.Fragment key={api.identifier}>
              <ListItem alignItems="flex-start" disableGutters sx={{ px: 2, py: 1.5 }}>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight={600}>
                      {api.identifier}
                    </Typography>
                  }
                  secondary={
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                        {
                          api.availablePermissions.map((perm) => {
                            const selected = isSelected(api.identifier, perm);
                            return (
                              <>
                                <Chip
                                  key={perm.value}
                                  label={perm.description === 'can access SOE-Cloud' ? 'can read SOE-Cloud' : perm.description}
                                  clickable
                                  onClick={() => toggle(api.identifier, perm)}
                                  color={selected ? "primary" : "default"}
                                  variant={selected ? "filled" : "outlined"}
                                  icon={selected ? <CheckIcon /> : undefined}
                                  disabled={disabled}
                                  sx={{ mr: 0.5 }}
                                  aria-pressed={selected}
                                />
                              </>
                            );
                          }
                        )}
                      </Stack>

                      <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={() => selectAll(api)}>
                          Select all
                        </Button>
                        <Button size="small" onClick={() => clearAll(api.identifier)}>
                          Clear
                        </Button>
                      </Stack>
                    </Stack>
                  }
                />
              </ListItem>
              {idx < data.length - 1 && <Divider component="li" />}
            </React.Fragment>
          )
        )}
      </List>
    </Paper>
  );
}
